/**
 * Perioada unei liste, ca o singura pastila („Perioada: Octombrie 2026 ▾") care deschide
 * variantele rapide si intervalul (Comenzi, 21.10.2026). Inlocuieste randul de cinci butoane plus
 * doua campuri de data, care umplea antetul si ramanea lipit peste lista.
 */
import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { QuickDateRange } from './DateField'

const LUNI = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
]

const ziRo = (s: string) => {
  const [y, m, d] = s.split('-')
  return `${d}.${m}.${y}`
}

/** Cum se citeste perioada aleasa: luna intreaga, anul intreg sau intervalul. */
export function numePerioada(de: string, pana: string): string {
  if (!de && !pana) return 'Toată perioada'
  if (de && pana) {
    const [y1, m1, d1] = de.split('-').map(Number)
    const [y2, m2, d2] = pana.split('-').map(Number)
    const ultimaZi = new Date(y2!, m2!, 0).getDate()
    if (y1 === y2 && m1 === m2 && d1 === 1 && d2 === ultimaZi) return `${LUNI[m1! - 1]} ${y1}`
    if (y1 === y2 && m1 === 1 && d1 === 1 && m2 === 12 && d2 === 31) return `Anul ${y1}`
    return `${ziRo(de)} – ${ziRo(pana)}`
  }
  return de ? `De la ${ziRo(de)}` : `Până la ${ziRo(pana)}`
}

export function FiltruPerioada({
  de,
  pana,
  onChange,
  aliniere = 'stanga',
}: {
  de: string
  pana: string
  onChange: (de: string, pana: string) => void
  /** „dreapta" cand pastila sta in dreapta antetului: panoul s-ar deschide in afara ecranului. */
  aliniere?: 'stanga' | 'dreapta'
}) {
  const [deschis, setDeschis] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!deschis) return
    const inafara = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setDeschis(false)
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setDeschis(false)
    document.addEventListener('mousedown', inafara)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', inafara)
      document.removeEventListener('keydown', esc)
    }
  }, [deschis])

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={deschis}
        onClick={() => setDeschis((d) => !d)}
        className="inline-flex items-center gap-1.5 rounded-full border border-b4l-blue bg-b4l-surface px-3 py-1.5 text-[13px] text-b4l-text hover:bg-b4l-bg-soft"
      >
        <span className="text-b4l-text-muted">Perioada:</span> {numePerioada(de, pana)}
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {deschis && (
        <div
          className={`absolute ${aliniere === 'dreapta' ? 'right-0' : 'left-0'} top-full z-30 mt-1.5 w-[min(92vw,520px)] rounded-xl border border-b4l-border bg-b4l-surface p-3 shadow-xl`}
        >
          <QuickDateRange
            fromValue={de}
            toValue={pana}
            onChange={(a, b) => {
              onChange(a, b)
            }}
          />
        </div>
      )}
    </div>
  )
}
